/**
 * THE ALMANAC PAGE — one builder, two runtimes.
 *
 * WHY THIS FILE EXISTS. The Academy has to work at sea, which means the almanac is computed on the
 * candidate's phone rather than fetched from here. The obvious way to do that is to write a browser
 * version of the /api/almanac handler. That way is wrong: two implementations of one page drift,
 * and the drift is invisible — both answer, both look like an almanac, and the day they disagree
 * nothing says which one the candidate should have believed.
 *
 * So the handler moved here. serve.mjs calls it; the browser calls it; there is no second version
 * to keep in step. What remains to be proved is that the SAME code gives the SAME answer in two
 * runtimes — floating point, date parsing and engine version can all differ between node and a
 * phone — and that is what almanac-cross-engine.mjs measures, to the digit.
 *
 * The ephemeris underneath is nav-tables.mjs, gated at 22/22 against pyephem reference sets
 * (nav-tables-verify.mjs). Nothing here is tabulated and nothing is cached: given an instant, the
 * page is COMPUTED. That is why offline needs no almanac download and has no expiry date — the
 * usual reason a paper almanac runs out is that somebody had to print it.
 */
import * as ALM from './nav-tables.mjs';

export function almanacBody({ date, stars = 8, lat = null, lon = null }) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return { kind: 'null', reason: 'unparseable date' };

  const nStars = Math.min(Number(stars) || 8, 58);
  const sun = ALM.sunVSOP(d), moon = ALM.moonPos(d), st = ALM.stars(d);
  const body = {
    kind: 'almanac', utc: d.toISOString(),
    gha_aries: st.gha_aries, gha_aries_dm: st.gha_aries_dm,
    sun: { gha_dm: sun.gha_dm, dec_dm: sun.dec_dm, gha: sun.gha, dec: sun.dec,
           sd_min: sun.semi_diameter_min, eot_min: sun.equation_of_time_min },
    moon: { gha_dm: moon.gha_dm, dec_dm: moon.dec_dm, gha: moon.gha, dec: moon.dec,
            hp_min: moon.hp_min, sd_min: moon.sd_min, phase: moon.illuminated_fraction },
    planets: ALM.planets(d).planets.map((x) => ({ body: x.body, gha_dm: x.gha_dm, dec_dm: x.dec_dm,
      gha: x.gha, dec: x.dec, distance_au: x.distance_au })),
    // brightest first — the ones a candidate can actually find at twilight
    stars: [...st.stars].sort((a, b) => a.mag - b.mag).slice(0, nStars)
      .map((x) => ({ name: x.name, mag: x.mag, sha_dm: x.sha_dm, dec_dm: x.dec_dm, sha: x.sha, dec: x.dec })),
    accuracy: 'measured inside 0.1\' against an independent implementation — sun 0.011\', moon 0.178\', '
      + 'stars 0.022\', planets 0.006-0.019\'',
    honesty: 'COMPUTED, not an official almanac. No figure is transcribed from one and nothing here is '
      + 'certified by an issuing authority. Learn with it, practise with it, check your arithmetic against '
      + 'it — a real fix is worked from the almanac carried aboard.',
  };

  // a worked practice sight if the observer says where he is
  if (lat !== null && lon !== null && lat !== '' && lon !== '') {
    body.practice_sight = ALM.practiceSight({ date: d, lat: +lat, lon: +lon });
  }
  return body;
}
